'use es6';

import { useEffect, useRef } from 'react';
import useInlineTarget from '../utils/hooks/useInlineTarget';
import { HS_INPAGE_SETTINGS } from '../constants/hsSettings';
import { useTinyMcePopoverLifecycle } from '../hooks/useTinyMcePopoverLifecycle';
import EmojiPickerWrapper from 'ui-addon-emoji-picker/picker/EmojiPickerWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
export default function EmojiPopoverManager(props) {
  const {
    editor,
    EmojiPicker,
    storeHandlers,
    onInsert,
    onOpenChange
  } = props;
  const {
    inlineTarget,
    setInlineTarget,
    initInlineTarget,
    insertAtInlineTarget,
    refreshKey
  } = useInlineTarget(editor, null);
  const lastTarget = useRef(inlineTarget);
  const popoverRef = useRef(null);
  useEffect(() => {
    lastTarget.current = inlineTarget;
  });
  useEffect(() => {
    const handleOpenPopover = () => {
      if (lastTarget.current) {
        setInlineTarget(null, {
          restoreSelection: true
        });
      } else {
        if (editor.settings[HS_INPAGE_SETTINGS]) {
          storeHandlers.init(() => initInlineTarget({
            fireNodeChange: true
          }));
        } else {
          initInlineTarget({
            fireNodeChange: true
          });
        }
      }
    };
    editor.on('OpenEmojiPopover', handleOpenPopover);
    editor.on('PreloadEmojiPicker', EmojiPicker.preload);
    return () => {
      editor.off('OpenEmojiPopover', handleOpenPopover);
      editor.off('PreloadEmojiPicker', EmojiPicker.preload);
    };
  }, [editor, EmojiPicker, storeHandlers, initInlineTarget, setInlineTarget]);
  useEffect(() => {
    onOpenChange(!!inlineTarget);
  }, [onOpenChange, inlineTarget]);
  const handleRemove = () => {
    if (lastTarget.current) {
      setInlineTarget(null);
    }
  };
  const handleInsert = (...args) => {
    insertAtInlineTarget(onInsert.bind(null, ...args));
    setTimeout(() => {
      var _popoverRef$current;
      // This is a workaround to prevent the emoji picker from being closed
      // when the emoji is inserted because the editor triggers the blur event
      (_popoverRef$current = popoverRef.current) === null || _popoverRef$current === void 0 || _popoverRef$current.focus();
    });
  };

  // Don't close if opened by the toolbar button or an emoji is clicked
  const shouldCloseOnClick = evt => {
    const isToolbarButton = evt && evt.target && editor.dom.getParent(evt.target, '.mce-toolbar,.mce-hubspot-popover,.mce-hubspot-dropdown');
    return !isToolbarButton;
  };
  useTinyMcePopoverLifecycle({
    editor,
    onRemove: handleRemove
  });
  const popoverProps = {
    open: !!inlineTarget,
    autoPlacement: 'vert',
    animateOnToggle: false,
    arrowSize: 'medium',
    placement: 'bottom right',
    closeOnOutsideClick: true,
    onOpenChange: handleRemove,
    'data-test-id': 'emoji-popover-manager',
    tabIndex: -1,
    shouldCloseOnClick,
    target: inlineTarget,
    'data-tinymce-floating-element': true,
    key: refreshKey
  };

  // TODO: Add onSelect prop, notFound custom emoji when emoji-mart bender-repackage is updated
  return /*#__PURE__*/_jsx(EmojiPickerWrapper, {
    ref: popoverRef,
    native: true,
    autoFocus: true,
    onClick: handleInsert,
    popoverProps: popoverProps,
    useDefaultFilter: true,
    unicodeVersion: 8.0,
    storeHandlers: storeHandlers,
    PickerContent: EmojiPicker
  });
}